/**
 * V4 Data Helper Utilities
 * 
 * Lightweight utilities for working with V4 target-centric analysis data.
 * Designed to be minimal but extensible for future needs.
 * 
 * Philosophy:
 * - V4 data is designed to be simple enough to use directly
 * - Add helpers only when the same pattern is used 3+ times
 * - Keep functions focused and composable
 * - Future-proof for V5+ by using generic patterns
 */

// ============================================================================
// VERSION DETECTION
// ============================================================================

/**
 * Check if data is V4 format
 */
export const isV4Data = (data) => {
  return data?.version === 4 || (Array.isArray(data?.targets) && data.targets.length > 0);
};

/**
 * Check if data is V3 format (legacy)
 */
export const isV3Data = (data) => {
  return data?.version === 3;
};

/**
 * Get data version (returns 3, 4, or null)
 */
export const getDataVersion = (data) => {
  if (isV4Data(data)) return 4;
  if (isV3Data(data)) return 3;
  return null;
};

// ============================================================================
// BASIC TARGET EXTRACTION (Simple Filters)
// ============================================================================

/**
 * Get all targets of a specific type
 * @param {Object} data - V4 analysis data
 * @param {String} type - Target type: 'vehicle', 'person', 'environment', 'videographer'
 * @returns {Array} Array of targets
 */
export const getTargetsByType = (data, type) => {
  if (!isV4Data(data)) {
    console.warn(`⚠️ Data is not V4 format (version: ${data?.version}). Expected V4 targets.`);
    return [];
  }
  
  return data.targets?.filter(t => t.type === type) || [];
};

/**
 * Get vehicles from V4 data
 */
export const getVehicles = (data) => getTargetsByType(data, 'vehicle');

/**
 * Get people from V4 data
 */
export const getPeople = (data) => getTargetsByType(data, 'person');

/**
 * Get environment target from V4 data (typically single target)
 */
export const getEnvironment = (data) => {
  const env = getTargetsByType(data, 'environment');
  return env[0] || null; // Usually only one environment
};

/**
 * Get videographer target from V4 data (typically single target)
 */
export const getVideographer = (data) => {
  const vid = getTargetsByType(data, 'videographer');
  return vid[0] || null; // Usually only one videographer
};

/**
 * Get all targets (any type)
 */
export const getAllTargets = (data) => {
  if (!isV4Data(data)) return [];
  return data.targets || [];
};

// ============================================================================
// DISPLAY HELPERS (For UI Formatting)
// ============================================================================

/**
 * Generate display name for a vehicle
 * @param {Object} vehicle - Vehicle target
 * @returns {String} Human-readable name like "Black Tesla Model 3"
 */
export const getVehicleDisplayName = (vehicle) => {
  if (!vehicle?.attributes) return 'Vehicle';
  
  const { color, make, model } = vehicle.attributes;
  const parts = [color, make, model].filter(Boolean);
  
  return parts.length > 0 ? parts.join(' ') : 'Vehicle';
};

/**
 * Format confidence score as percentage
 * @param {Number} confidence - Confidence value (0.0 to 1.0)
 * @returns {String} Formatted percentage like "85%"
 */
export const formatConfidence = (confidence) => {
  if (typeof confidence !== 'number') return '0%';
  return Math.round(confidence * 100) + '%';
};

/**
 * Get short description for a target
 * @param {Object} target - Any V4 target
 * @returns {String} Brief description
 */
export const getTargetDescription = (target) => {
  if (!target) return 'Unknown';
  
  switch (target.type) {
    case 'vehicle':
      return getVehicleDisplayName(target);
    case 'person':
      return target.attributes?.description || 'Person';
    case 'environment':
      return target.attributes?.location_type || 'Environment';
    case 'videographer':
      return target.attributes?.device_type || 'Camera';
    default:
      return target.type || 'Target';
  }
};

// ============================================================================
// FILTERING & SORTING (Reusable Query Patterns)
// ============================================================================

/**
 * Filter targets by confidence threshold
 * @param {Array} targets - Array of targets
 * @param {Number} minConfidence - Minimum confidence (0.0 to 1.0)
 * @returns {Array} Filtered targets
 */
export const filterByConfidence = (targets, minConfidence) => {
  return targets.filter(t => (t.confidence || 0) >= minConfidence);
};

/**
 * Sort targets by confidence (highest first)
 */
export const sortByConfidence = (targets) => {
  return [...targets].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
};

/**
 * Get top N targets by confidence
 */
export const getTopTargets = (targets, limit = 5) => {
  return sortByConfidence(targets).slice(0, limit);
};

// ============================================================================
// RELATIONSHIP HELPERS (Navigate Connections)
// ============================================================================

/**
 * Find targets related to a given target
 * @param {Object} data - V4 analysis data
 * @param {Object} target - Target to find relationships for
 * @param {String} relationshipType - Optional: filter by relationship type
 * @returns {Array} Array of related targets with relationship info
 */
export const getRelatedTargets = (data, target, relationshipType = null) => {
  if (!target?.actions?.relationships) return [];
  
  const relationships = target.actions.relationships;
  const filteredRels = relationshipType 
    ? relationships.filter(r => r.type === relationshipType)
    : relationships;
  
  return filteredRels.map(rel => {
    // Find the connected target
    const connectedTarget = data.targets?.find(t => t.id === rel.connected_to);
    
    return {
      relationship: rel,
      target: connectedTarget,
      relationshipType: rel.type,
      description: rel.description || rel.nature
    };
  }).filter(r => r.target); // Only include if target found
};

/**
 * Check if two targets are related
 */
export const areTargetsRelated = (target1, target2) => {
  if (!target1?.actions?.relationships || !target2?.id) return false;
  
  return target1.actions.relationships.some(r => r.connected_to === target2.id);
};

// ============================================================================
// TIMELINE HELPERS (Temporal Data)
// ============================================================================

/**
 * Get all activities for a target
 */
export const getTargetActivities = (target) => {
  return target?.actions?.activities || [];
};

/**
 * Get master timeline events
 */
export const getMasterTimeline = (data) => {
  if (!isV4Data(data)) return [];
  return data.master_timeline || [];
};

/**
 * Get timeline events for a specific target
 */
export const getTargetTimelineEvents = (data, targetId) => {
  const timeline = getMasterTimeline(data);
  return timeline.filter(event => event.target_id === targetId);
};

// ============================================================================
// SUMMARY & STATS (Aggregate Data)
// ============================================================================

/**
 * Get count of targets by type
 * @returns {Object} { vehicles: 2, people: 3, ... }
 */
export const getTargetCounts = (data) => {
  if (!isV4Data(data)) return {};
  
  const counts = {};
  data.targets?.forEach(target => {
    const type = target.type || 'unknown';
    counts[type] = (counts[type] || 0) + 1;
  });
  
  return counts;
};

/**
 * Get overall analysis summary
 */
export const getAnalysisSummary = (data) => {
  if (!isV4Data(data)) {
    return {
      version: getDataVersion(data),
      isSupported: false,
      totalTargets: 0,
      targetCounts: {}
    };
  }
  
  return {
    version: 4,
    isSupported: true,
    totalTargets: data.targets?.length || 0,
    targetCounts: getTargetCounts(data),
    forensicSummary: data.forensic_summary || data.summary?.key_finding || null,
    confidence: data.summary?.confidence_overall || null,
    duration: data.video_duration || null
  };
};

// ============================================================================
// VALIDATION HELPERS (Data Quality Checks)
// ============================================================================

/**
 * Check if a target has minimum required data
 */
export const isTargetValid = (target) => {
  return !!(target?.id && target?.type && target?.attributes);
};

/**
 * Filter out invalid targets
 */
export const getValidTargets = (data) => {
  const targets = getAllTargets(data);
  return targets.filter(isTargetValid);
};

/**
 * Check if vehicle has license plate data
 */
export const hasLicensePlate = (vehicle) => {
  const plate = vehicle?.attributes?.license_plate;
  return !!(plate && plate !== 'not visible' && plate !== 'unknown');
};

/**
 * Check if vehicle has damage
 */
export const hasDamage = (vehicle) => {
  const damage = vehicle?.attributes?.damage;
  return !!(damage && damage !== 'none' && damage !== 'unknown');
};

// ============================================================================
// FUTURE EXTENSION PATTERNS
// ============================================================================

/**
 * Generic extractor pattern for future use
 * Example: Extract any field across all targets
 * 
 * @param {Object} data - Analysis data
 * @param {Function} extractorFn - Function to extract field from target
 * @returns {Array} Extracted values
 */
export const extractFromTargets = (data, extractorFn) => {
  const targets = getAllTargets(data);
  return targets.map(extractorFn).filter(Boolean);
};

/**
 * Generic filter pattern for future use
 * 
 * @param {Object} data - Analysis data
 * @param {Function} predicateFn - Filter function
 * @returns {Array} Filtered targets
 */
export const filterTargets = (data, predicateFn) => {
  const targets = getAllTargets(data);
  return targets.filter(predicateFn);
};

// ============================================================================
// EXPORT ALL HELPERS
// ============================================================================

const v4DataHelpers = {
  // Version detection
  isV4Data,
  isV3Data,
  getDataVersion,
  
  // Basic extraction
  getTargetsByType,
  getVehicles,
  getPeople,
  getEnvironment,
  getVideographer,
  getAllTargets,
  
  // Display helpers
  getVehicleDisplayName,
  formatConfidence,
  getTargetDescription,
  
  // Filtering & sorting
  filterByConfidence,
  sortByConfidence,
  getTopTargets,
  
  // Relationships
  getRelatedTargets,
  areTargetsRelated,
  
  // Timeline
  getTargetActivities,
  getMasterTimeline,
  getTargetTimelineEvents,
  
  // Summary & stats
  getTargetCounts,
  getAnalysisSummary,
  
  // Validation
  isTargetValid,
  getValidTargets,
  hasLicensePlate,
  hasDamage,
  
  // Future patterns
  extractFromTargets,
  filterTargets
};

export default v4DataHelpers;

